import React, { useMemo } from 'react'
import { Box, useTheme } from '@mui/material'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'

/**
 * ThroughputChart — a small local time-series chart for the AutoCoder
 * throughput panels (the admin overview and the per-pipeline tab).
 *
 * Deliberately NOT PlatinumGraphs: that primitive consumes PlatinumData, the
 * Carbon crosstab response shape, and a {lane, tokensPerMin} series does not
 * fit it without pointless reshaping. This is a thin wrapper over
 * echarts-for-react (already a dependency), scoped to this page.
 */

export interface ThroughputSeries {
  name: string
  /** [timestampMs, value] pairs, already zero-filled by the server. */
  points: [number, number][]
  /** Unknown/unrecognised series render muted and dashed. */
  muted?: boolean
  /** Explicit colour, for series whose colour carries meaning (queue depth
   *  reuses the pending/running chip colours). Otherwise the ramp assigns one. */
  color?: string
}

interface ThroughputChartProps {
  series: ThroughputSeries[]
  height?: number
  /** 'area' stacks the series (total throughput); 'bar' is discrete counts. */
  variant?: 'area' | 'bar'
  valueFormatter: (value: number) => string
  yAxisName?: string
  emptyMessage?: string
}

/**
 * Categorical ramp anchored on the info-blue brand hue, stepping through
 * neighbouring hues rather than the full rainbow — bold, not loud. Muted
 * series bypass this and take a neutral grey.
 */
const LANE_COLORS = [
  '#2E7DF7',
  '#00A3B5',
  '#7A5AF8',
  '#0F9D58',
  '#E8710A',
  '#C2306E',
  '#4A6FA5',
  '#1FA2A6',
  '#9B51E0',
  '#5B8C00',
]

const ThroughputChart: React.FC<ThroughputChartProps> = ({
  series,
  height = 280,
  variant = 'area',
  valueFormatter,
  yAxisName,
  emptyMessage = 'No activity in this window',
}) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  const hasData = series.some((s) => s.points.some(([, v]) => v > 0))

  const option: EChartsOption = useMemo(() => {
    const axisColor = theme.palette.text.secondary
    const splitColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'
    let colorIndex = 0

    return {
      backgroundColor: 'transparent',
      grid: { left: 56, right: 16, top: 28, bottom: series.length > 1 ? 48 : 28 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: variant === 'bar' ? 'shadow' : 'line' },
        valueFormatter: (value) => valueFormatter(Number(value)),
      },
      legend:
        series.length > 1
          ? {
              bottom: 0,
              type: 'scroll',
              textStyle: { color: axisColor, fontSize: 11 },
              icon: 'roundRect',
              itemHeight: 8,
              itemWidth: 12,
            }
          : undefined,
      xAxis: {
        type: 'time',
        axisLine: { lineStyle: { color: splitColor } },
        axisLabel: { color: axisColor, fontSize: 11, hideOverlap: true },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        name: yAxisName,
        nameTextStyle: { color: axisColor, fontSize: 11, align: 'left' },
        axisLabel: { color: axisColor, fontSize: 11, formatter: (v: number) => valueFormatter(v) },
        splitLine: { lineStyle: { color: splitColor } },
      },
      series: series.map((s) => {
        const color =
          s.color ??
          (s.muted ? theme.palette.text.disabled : LANE_COLORS[colorIndex++ % LANE_COLORS.length])
        if (variant === 'bar') {
          return {
            name: s.name,
            type: 'bar' as const,
            stack: 'total',
            data: s.points,
            itemStyle: { color, borderRadius: [2, 2, 0, 0] },
          }
        }
        return {
          name: s.name,
          type: 'line' as const,
          stack: 'total',
          smooth: 0.2,
          showSymbol: false,
          data: s.points,
          lineStyle: { width: 1.5, color, type: s.muted ? 'dashed' : 'solid' },
          itemStyle: { color },
          areaStyle: { color, opacity: s.muted ? 0.12 : 0.28 },
        }
      }),
    } as EChartsOption
  }, [series, theme, isDark, variant, valueFormatter, yAxisName])

  if (!hasData) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.disabled',
          fontSize: 14,
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          borderStyle: 'dashed',
        }}
      >
        {emptyMessage}
      </Box>
    )
  }

  return (
    <ReactECharts
      option={option}
      style={{ height, width: '100%' }}
      opts={{ renderer: 'svg' }}
      notMerge
    />
  )
}

export default ThroughputChart
